/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { ColorProps } from '#utils/UNSAFE_interpolations/colors';
import { LineAreaItem } from '#UNSAFE_LineAreaChart/lineAreaChart.types';
import { Marker, MARKER_PADDING } from '#PRIVATE_SvgShapes';
import { MarkerShapes } from '#utils/UNSAFE_visTypes/common';
import { getGroupCenterCoord } from '#utils/PRIVATE_chartUtils/layoutUtils';
import { ChartSeriesData, Scale, Group } from '#utils/UNSAFE_visTypes/chart';
import { getMarkerFillAndStroke } from '#utils/PRIVATE_chartUtils/utils';
import { supportsMobileScreenReader } from '#utils/UNSAFE_visUtils';
import { MarkerLabel } from './MarkerLabel';
import { styles } from '#PRIVATE_Chart/themes/ChartStyles.css';
import { Dimension } from '#utils/UNSAFE_visTypes/common';
import { Font } from '#hooks/PRIVATE_useTextDimensions';
import { ScatterItem } from '#UNSAFE_ScatterChart/scatterChart.types';

const MARKER_DEFAULT_SIZE = 10;
type MarkersProps<K, D> = {
  yScale: Scale;
  xScale: Scale;
  startIndex: number;
  endIndex: number;
  seriesIndex: number;
  series: ChartSeriesData<K, D>;
  markerType: MarkerShapes;
  color: ColorProps['color'];
  groups: Group[];
  focusedItemIndex?: number;
  isSelectionEnabled: boolean;
  hoveredItemIndex?: number;
  selectedIds?: Set<string | number>;
  hiddenIds?: Set<string | number>;
  highlightedIds?: Set<string | number>;
  activeId?: string;
  orientation: 'horizontal' | 'vertical';
  isDrillEnabled: boolean;
  isLog: boolean;
  isPointInsideMarquee: ((id: K, point: { x: number; y: number }) => boolean) | undefined;
  getDataItem: (seriesIndex: number, groupIndex: number) => D | undefined;
  timeAxisType?: 'enabled' | 'skipGaps' | 'mixedFrequency';
  defaultFontSize: string;
  dataLabelEffect: 'outline' | 'none';
  isHighContrast: boolean;
  isTestEnv?: boolean;
  isDataXAxis?: boolean;
  getTextDimensions?: (text: string, style: Font) => Dimension;
  itemValueFormatter?: (value: number) => string;
  getItemAriaLabel: (
    data: D,
    seriesIndex: number,
    groupIndex: number,
    isSelectionEnabled: boolean,
    isSelected: boolean,
    isDrillable: boolean
  ) => string;
  isRtl: boolean;
};

export function MarkersWithLabel<
  K extends string | number,
  D extends LineAreaItem<K> | ScatterItem<K>
>({
  yScale,
  xScale,
  color,
  seriesIndex,
  startIndex,
  endIndex,
  hiddenIds,
  selectedIds,
  highlightedIds,
  orientation,
  isLog,
  markerType,
  getDataItem,
  focusedItemIndex,
  hoveredItemIndex,
  isDrillEnabled,
  activeId,
  isSelectionEnabled,
  timeAxisType,
  isPointInsideMarquee,
  groups,
  defaultFontSize,
  dataLabelEffect,
  isHighContrast,
  isTestEnv,
  isDataXAxis,
  getTextDimensions,
  getItemAriaLabel,
  isRtl
}: MarkersProps<K, D>) {
  const isHoriz = orientation === 'horizontal';
  const items = [];
  for (let groupIndex = startIndex; groupIndex < endIndex + 1; groupIndex++) {
    const item = getDataItem(seriesIndex, groupIndex);
    if (!item || hiddenIds?.has(item.id)) {
      continue;
    }
    let x, y;
    const itemHasValue = 'value' in item;
    if (isDataXAxis && 'y' in item) {
      x = xScale.transform(item.x);
      y = yScale.transform(item.y);
    } else {
      const value = itemHasValue ? item?.value : undefined;
      if (value == null || (isLog && value <= 0)) {
        continue;
      }
      x = isHoriz
        ? yScale.transform(value)
        : getGroupCenterCoord<K, D>(groups[groupIndex], groupIndex, xScale, item, timeAxisType);
      y = isHoriz
        ? getGroupCenterCoord<K, D>(groups[groupIndex], groupIndex, xScale, item, timeAxisType)
        : yScale.transform(value);
    }
    const isFocused = focusedItemIndex === groupIndex;
    const isHovered = hoveredItemIndex === groupIndex;
    const isSelected = !!(selectedIds?.has(item.id!) || isPointInsideMarquee?.(item.id, { x, y })); // TODO: tagcloud has isItemSelected we should look to leverage here
    const isDrillable = item.drilling === 'on' || (item.drilling != 'off' && isDrillEnabled);
    const isHighlighted =
      !highlightedIds || highlightedIds?.size === 0 || highlightedIds?.has(item.id); // TODO: tagcloud has isItemHighlighted we should look to leverage here
    const { fill, stroke, outerStroke } = getMarkerFillAndStroke(
      !!item.isMarkerDisplayed,
      isSelectionEnabled,
      isHovered,
      isFocused,
      isSelected,
      color,
      item.markerColor
    );
    const itemMarkerType = item.isMarkerDisplayed ? item.markerType || markerType : markerType;
    const isActive = isFocused || isHovered;
    const ariaLabel =
      supportsMobileScreenReader || isActive
        ? getItemAriaLabel(
            item,
            seriesIndex,
            groupIndex,
            isSelectionEnabled,
            !!isSelected,
            isDrillable
          )
        : undefined;

    const dataInfo = {
      'data-oj-private-series-index': seriesIndex,
      'data-oj-private-group-index': groupIndex,
      'data-oj-private-object': 'item',
      id: isActive ? activeId : undefined,
      role: ariaLabel ? 'img' : undefined,
      'aria-label': ariaLabel,
      'data-oj-private-item-id': isTestEnv ? item.id : undefined
    };

    const markerSize = (item.markerSize || MARKER_DEFAULT_SIZE) + 2 * MARKER_PADDING;
    items.push(
      <>
        <Marker
          type={itemMarkerType}
          tx={x}
          ty={y}
          isInteractive={isDrillable || isSelectionEnabled}
          scale={1}
          fill={fill}
          stroke={stroke}
          outerStroke={outerStroke}
          width={markerSize}
          height={markerSize}
          dataInfo={dataInfo}
          preserveRectAspectRatio
          styleClass={isHighlighted ? undefined : styles.dimmed}
        />
        <MarkerLabel
          labelPosition={item.labelPosition}
          label={item.label}
          labelStyle={item.labelStyle}
          tx={x}
          ty={y}
          defaultFontSize={defaultFontSize}
          dataLabelEffect={dataLabelEffect}
          isHighContrast={isHighContrast}
          getTextDimensions={getTextDimensions}
          isRtl={isRtl}
        />
      </>
    );
  }
  return <>{items}</>;
}
